import React from 'react';
import { Typography, Skeleton, Box, Button, Divider } from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import PermissionsMatrix from '@/components/PermissionsMatrix/PermissionsMatrix';
import { useGetFacilityPermissionSchema } from '@/api/hooks/roles/useGetFacilityPermissionSchema';
import { useGetOrganizationPermissionSchema } from '@/api/hooks/roles/useGetOrganizationPermissionSchema';

const facilityTemplates = [
  {
    id: 'admin_template',
    name: 'Administrator',
    description: 'Full access to all features',
    facilityPermissions: {
      refund_reporting: {
        refund_reporting: ['read', 'download'],
        viewPage: ['read'],
      },
      business_units: {
        business_units: ['read', 'write'],
        viewPage: ['read'],
      },
      refundee_file: {
        refundee_file: ['read', 'write'],
        viewPage: ['read'],
      },
      email_template: {
        email_template: ['read', 'write'],
        viewPage: ['read'],
      },
      transaction_page: {
        payment_status: ['write'],
        refund_information: ['read', 'write', 'download'],
        send_payment: ['read', 'write'],
        move_to_unclaimed: ['read', 'write'],
        move_to_ach_returned: ['read', 'write'],
        cancel_refund: ['read', 'write'],
        void_check: ['read', 'write'],
        status_history: ['read'],
        viewPage: ['read'],
      },
      create_payment: {
        create_payment: ['write'],
        viewPage: ['read'],
      },
      approvals: {
        payment_information: ['read', 'write'],
        approve_payment: ['write'],
        viewPage: ['read'],
      },
      exceptions: {
        processing_exceptions: ['read', 'write'],
        viewPage: ['read'],
      },
    },
  },
  {
    id: 'manager_template',
    name: 'Manager',
    description: 'Manage operations with limited admin access',
    facilityPermissions: {
      refund_reporting: {
        refund_reporting: ['read', 'download'],
        viewPage: ['read'],
      },
      business_units: {
        business_units: ['read'],
        viewPage: ['read'],
      },
      transaction_page: {
        refund_information: ['read'],
        status_history: ['read'],
        viewPage: ['read'],
      },
      approvals: {
        payment_information: ['read', 'write'],
        approve_payment: ['write'],
        viewPage: ['read'],
      },
    },
  },
  {
    id: 'viewer_template',
    name: 'Viewer',
    description: 'Read-only access to data and reports',
    facilityPermissions: {
      refund_reporting: {
        refund_reporting: ['read'],
        viewPage: ['read'],
      },
      unclaimed_refunds: {
        unclaimed_refunds: ['read'],
        viewPage: ['read'],
      },
      transaction_page: {
        refund_information: ['read'],
        status_history: ['read'],
        viewPage: ['read'],
      },
    },
  },
];

const organizationTemplates = [
  {
    id: 'org_admin_template',
    name: 'Organization Admin',
    description: 'Full organization-level access',
    organizationPermissions: {
      data_mapping: {
        column_headers: ['read', 'write'],
        data_mapping_groups: ['read', 'write'],
        viewPage: ['read'],
      },
      audience: {
        audiences: ['read', 'write'],
        viewPage: ['read'],
      },
    },
  },
  {
    id: 'org_manager_template',
    name: 'Organization Manager',
    description: 'Limited organization management',
    organizationPermissions: {
      data_mapping: {
        column_headers: ['read', 'write'],
        data_mapping_groups: ['read', 'write'],
        viewPage: ['read'],
      },
      audience: {
        audiences: ['read'],
        viewPage: ['read'],
      },
    },
  },
  {
    id: 'org_viewer_template',
    name: 'Organization Viewer',
    description: 'Read-only organization access',
    organizationPermissions: {
      data_mapping: {
        column_headers: ['read', 'write'],
        data_mapping_groups: ['read', 'write'],
        viewPage: ['read'],
      },
    },
  },
];

const PermissionsSection = ({
  permissions,
  onPermissionChange,
  onApplyTemplate,
  permissionLevel = 'facility',
  onMirrorClick,
}) => {
  const { t } = useTranslation();
  const {
    facilityPermissionSchema,
    loading: facilityLoading,
    error: facilityError,
  } = useGetFacilityPermissionSchema();

  const {
    organizationPermissionSchema,
    loading: organizationLoading,
    error: organizationError,
  } = useGetOrganizationPermissionSchema();

  const permissionSchema =
    permissionLevel === 'organization'
      ? organizationPermissionSchema
      : facilityPermissionSchema;

  const schemaLoading =
    permissionLevel === 'organization' ? organizationLoading : facilityLoading;

  const schemaError =
    permissionLevel === 'organization' ? organizationError : facilityError;

  const roleTemplates =
    permissionLevel === 'organization'
      ? organizationTemplates
      : facilityTemplates;

  const handlePermissionToggle = (
    templateKey,
    childKey,
    permission,
    checked,
  ) => {
    const newPermissions = {
      ...permissions,
      [templateKey]: {
        ...permissions[templateKey],
        [childKey]: checked
          ? [...(permissions[templateKey]?.[childKey] || []), permission]
          : permissions[templateKey]?.[childKey]?.filter(
              (p) => p !== permission,
            ) || [],
      },
    };
    onPermissionChange(newPermissions);
  };

  if (schemaError) {
    return (
      <Typography color='error' variant='body2' sx={{ mb: 2 }}>
        Error loading permissions: {schemaError}
      </Typography>
    );
  }

  if (schemaLoading) {
    return (
      <Box>
        <Typography variant='h6' gutterBottom>
          Quick Templates
        </Typography>
        <Skeleton variant='rounded' height={60} width='100%' sx={{ mb: 3 }} />
        <Typography variant='h6' gutterBottom>
          {permissionLevel === 'organization'
            ? 'Organization Permissions'
            : 'Facility Permissions'}
        </Typography>
        <Skeleton variant='rounded' height={200} width='100%' />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant='h6' gutterBottom>
          Quick Templates
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {roleTemplates.map((template) => (
            <Button
              key={template.id}
              variant='outlined'
              size='small'
              onClick={() => onApplyTemplate && onApplyTemplate(template)}>
              {template.name}
            </Button>
          ))}
        </Box>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          Click a template to apply its permissions to this role
        </Typography>

        {onMirrorClick && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant='outlined'
              size='small'
              startIcon={<ContentCopyIcon />}
              onClick={onMirrorClick}>
              {t('mirrorPermissions.button')}
            </Button>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
              {t('mirrorPermissions.buttonDescription')}
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      <PermissionsMatrix
        permissions={permissions}
        onPermissionChange={handlePermissionToggle}
        permissionSchema={permissionSchema}
        loading={schemaLoading}
        title={
          permissionLevel === 'organization'
            ? 'Organization Permissions'
            : 'Facility Permissions'
        }
      />
    </Box>
  );
};

export default PermissionsSection;
